export const VENDA_ONLINE_LISTA_STATUS = {
  aberto: { rotulo: "Aberto", cor: "green-7", icone: "link" },
  cancelando: { rotulo: "Cancelando", cor: "orange-8", icone: "hourglass_top" },
  liquidado: { rotulo: "Liquidado", cor: "positive", icone: "check_circle" },
  expirado: { rotulo: "Expirado", cor: "grey-7", icone: "schedule" },
  cancelado: { rotulo: "Cancelado", cor: "grey-7", icone: "block" },
  conflito: { rotulo: "Conflito", cor: "red-7", icone: "report" },
  gerando: { rotulo: "Gerando", cor: "blue-7", icone: "sync" }
};

const STATUS_DESCONHECIDO = { rotulo: "Status desconhecido", cor: "grey-7", icone: "help" };

const STATUS_NA_LISTA = {
  pendente_geracao: "gerando",
  registrando: "gerando",
  ativa: "aberto",
  cancelando: "cancelando",
  paga: "liquidado",
  expirada: "expirado",
  cancelada: "cancelado"
};

export const getVendaOnlineListaStatus = (chave) => VENDA_ONLINE_LISTA_STATUS[chave] || STATUS_DESCONHECIDO;

export const getVendaOnlineStatus = (status) => getVendaOnlineListaStatus(STATUS_NA_LISTA[status]);

export const VENDA_ONLINE_CANCELAVEIS = ["pendente_geracao", "registrando", "ativa"];

export const VENDA_ONLINE_ABERTAS = [...VENDA_ONLINE_CANCELAVEIS, "cancelando"];

export const fraseCancelamento = (quantidade) => `confirmo o cancelamento de ${quantidade} ${quantidade === 1 ? "venda" : "vendas"}`;

export const createChaveIdempotencia = () => {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const centavos = (valor) => Math.round(parseFloat(valor || 0) * 100);

const prorateDescontos = (itens, descontoGeral) => {
  const brutos = itens.map((item) => centavos(item.vlrvenda_unit) * Number(item.quantidade || 0));
  const totalBruto = brutos.reduce((soma, bruto) => soma + bruto, 0);
  const desconto = centavos(descontoGeral);
  if (totalBruto <= 0 || desconto <= 0) return itens.map(() => 0);

  const alvo = Math.min(desconto, totalBruto);
  const rateio = brutos.map((bruto) => Math.floor((alvo * bruto) / totalBruto));
  const fracoes = brutos.map((bruto, i) => ({ i, fracao: (alvo * bruto) % totalBruto }));
  let sobra = alvo - rateio.reduce((soma, parte) => soma + parte, 0);
  while (sobra > 0) {
    const comFolga = fracoes.filter(({ i }) => rateio[i] < brutos[i]).sort((a, b) => b.fracao - a.fracao || a.i - b.i);
    for (const { i } of comFolga) {
      if (sobra === 0) break;
      rateio[i] += 1;
      sobra -= 1;
    }
  }

  return rateio;
};

export const buildVendaOnlineItens = (movvendasItems, descontoGeral, descontoDetalhado) => {
  const itens = movvendasItems || [];
  const rateio = descontoDetalhado ? itens.map((item) => centavos(item.desconto)) : prorateDescontos(itens, descontoGeral);

  return itens.map((item, i) => ({
    produto_id: item.produto_id,
    ref_estoque: {
      tpcontrole: item.tpcontrole || null,
      concurso: item.concurso || null,
      extracao: item.extracao || null,
      vencimento: item.vencimento || null,
      evento_id: item.evento_id || null
    },
    quantidade: Number(item.quantidade),
    valor_unitario: centavos(item.vlrvenda_unit) / 100,
    valor_desconto: rateio[i] / 100
  }));
};

const splitMomento = (momento) => {
  const encontrado = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(String(momento || ""));
  if (!encontrado) return null;
  const [, ano, mes, dia, hora, minuto] = encontrado;

  return { ano, mes, dia, hora: hora ? `${hora}:${minuto}` : "" };
};

export const formatHora = (momento) => splitMomento(momento)?.hora || "";

export const REGISTRO_CAUSAS = {
  timeout: "sem resposta a tempo",
  limite: "limite de pedidos do pagamento online",
  sem_resposta: "sem resposta a tempo",
  recusada: "recusado pelo pagamento online",
  resposta_invalida: "resposta inválida do pagamento online",
  sem_credenciais: "pagamento online sem configuração",
  sandbox: "modo homologação"
};

export const formatRegistroTentativas = (venda) => {
  const tentativas = Number(venda?.tentativas_registro || 0);
  if (venda?.status !== "pendente_geracao" || tentativas <= 0) return "";
  const contagem = `${tentativas} ${tentativas === 1 ? "tentativa" : "tentativas"}`;
  if (venda.registro_parado) {
    return `Registro parado: ${REGISTRO_CAUSAS[venda.registro_parado] || venda.registro_parado} · ${contagem}`;
  }
  const hora = formatHora(venda.proxima_tentativa_em);

  return `${contagem}${hora ? ` · próxima às ${hora}` : ""}`;
};

export const CONFLITO_MOTIVOS = {
  pago_apos_cancelamento: "Pago depois do cancelamento",
  pago_apos_expiracao: "Pago depois da expiração",
  estoque_insuficiente: "Estoque insuficiente",
  pagamento_divergente: "Pagamento que não confere",
  valor_divergente: "Pago abaixo do valor da venda",
  valores_incoerentes: "Valores do pagamento incoerentes",
  taxa_acima_do_pago: "Taxa acima do valor pago",
  referencia_divergente: "Referência da cobrança não confere",
  registrada_apos_cancelamento: "Pix registrado depois do cancelamento",
  pagamento_nao_confirmado: "Pagamento não confirmado",
  pagamento_nao_aplicado: "Pagamento não aplicado",
  lancamento_falhou: "Lançamento no financeiro pendente",
  configuracao_incompleta: "Configuração incompleta para lançar",
  lancamento_cancelado_manual: "Lançamento cancelado no financeiro",
  corte_atrasado: "Link ativo depois do corte",
  corte_falhou: "Corte falhou",
  whatsapp_sem_modelo: "WhatsApp sem modelo de mensagem",
  aviso_orfao: "Aviso de pagamento sem venda",
  empresa_inativa: "Pago com a empresa inativa",
  baixa_pendente: "Baixa de estoque pendente",
  envio_link_falhou: "Envio do link pendente",
  status_pos_pago: "Status da cobrança depois de pago",
  status_desconhecido: "Status desconhecido da cobrança"
};

export const formatConflitoMotivo = (motivo) => CONFLITO_MOTIVOS[motivo] || String(motivo || "").replace(/_/g, " ");

export const AGUARDANDO_O_QUE_FAZER = {
  lancamento_falhou: "Confira o financeiro (plano de contas, conta e centro de custo). A rotina tenta de novo sozinha.",
  configuracao_incompleta: "Complete a configuração em Opções Avançadas (engrenagem) da página Vendas Online. Os lançamentos entram na próxima tentativa.",
  baixa_pendente: "Confira o estoque do produto. A rotina tenta a baixa de novo sozinha.",
  envio_link_falhou: "Confira o canal de envio. A rotina tenta de novo sozinha; você também pode reenviar o link.",
  empresa_inativa: "Os efeitos esperam a empresa voltar a ficar ativa, ou a decisão do operador.",
  corte_atrasado: "O link continua ativo depois do corte. A rotina tenta cortar de novo; se a venda encerrar, o aviso sai.",
  corte_falhou: "O corte está falhando. A rotina tenta de novo; se a venda encerrar, o aviso sai.",
  registro_parado: "O registro do Pix parou. Fale com o suporte DouraSoft ou cancele a venda; o aviso sai quando ela registrar ou encerrar."
};

const DOMINIOS_DOURASOFT = ["dourasoft.com.br", "dourasoft.net"];

export const isEquipeDourasoft = (usuario) => {
  const email = String(usuario?.email || "").trim().toLowerCase();
  const arroba = email.lastIndexOf("@");

  return arroba !== -1 && DOMINIOS_DOURASOFT.includes(email.slice(arroba + 1));
};

const PENDENCIAS_DA_EQUIPE = ["gateway_conta_recebimento"];

export const getPendenciaAcao = (pendencia, equipeDourasoft) => {
  if (PENDENCIAS_DA_EQUIPE.includes(pendencia?.item) && !equipeDourasoft) {
    return { rotulo: "Aguardando a DouraSoft", atalho: null };
  }

  return { rotulo: pendencia?.atalho === "opcoes" ? "Configurar" : "Abrir Parâmetros", atalho: pendencia?.atalho };
};

export const formatAguardando = (motivo) => AGUARDANDO_O_QUE_FAZER[motivo] || "A rotina tenta de novo sozinha.";

export const formatMomento = (momento) => {
  const partes = splitMomento(momento);
  if (!partes) return "";

  return `${partes.dia}/${partes.mes}/${partes.ano}${partes.hora ? ` ${partes.hora}` : ""}`;
};

const rascunhoChaves = new Map();

const buildPedidoRascunho = (pedido) => `${pedido.movcaixa_id}:${pedido.movvenda_id}`;

const buildPedidoAssinatura = (pedido) =>
  JSON.stringify([pedido.movcaixa_id, pedido.movvenda_id, pedido.cliente_id, pedido.itens, pedido.meio, pedido.taxa_conveniencia ?? null]);

export const getRascunhoChave = (pedido) => {
  const rascunho = buildPedidoRascunho(pedido);
  const assinatura = buildPedidoAssinatura(pedido);
  const guardada = rascunhoChaves.get(rascunho);
  if (guardada && guardada.assinatura === assinatura) return guardada.chave;

  const chave = createChaveIdempotencia();
  rascunhoChaves.set(rascunho, { assinatura, chave });

  return chave;
};

export const forgetRascunhoChave = (pedido) => {
  rascunhoChaves.delete(buildPedidoRascunho(pedido));
};

export const mensagemAbertasOcultas = (quantidade) => {
  const vendas = quantidade === 1 ? "venda online em aberto neste caixa feita" : "vendas online em aberto neste caixa feitas";

  return `Há ${quantidade} ${vendas} por outra operadora, que você não vê. Peça a quem tem a permissão Editar da Venda Online para transferir ou cancelar.`;
};
